import { Box, Typography, Button, Accordion, AccordionSummary, AccordionDetails, Divider, Paper, /* Remove IconButton, Menu */ MenuItem, CircularProgress, Select, FormControl, InputLabel, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../../axios/axios';
import DescIcon from '../../assets/risk_details_decs.svg'
import RectICON from '../../assets/risk_details_rect.svg'
import IssuesIcon from '../../assets/risk_details_issues.svg'
import Http from '../../assets/risk_details_http.svg'
import sparkLogo from '../../assets/Spark-text.svg'
//import cisLogo from '../../assets/cis.png'
import cisLogo from "../../assets/cis_light.svg"
import nistLogo from '../../assets/nist.svg'
import Remediation_img from '../../assets/risk_details_rect.svg'
import Audit_img from '../../assets/risk_details_issues.svg'
import Rational_img from '../../assets/risk_details_decs.svg'
import DetailsFlyout from './Details-Flyout';


type Framework = 'cis' | 'nist' | 'spark';

// Interface for structured version data
interface VersionInfo {
    vendor: string;
    model: string;
    version: string;
}

type SeeDetailsDrawerProps = {
    handlleSeeDetailsClose: (value: boolean) => void;
    selectedRisk: any // update type as per your actual data
};

const SeeDetailsDrawer = ({ handlleSeeDetailsClose, selectedRisk }: SeeDetailsDrawerProps) => {
    console.log("selectedRisk", selectedRisk);
    const [totalDetails, setTotalDetails] = useState<any>(null);
    const [controlDesc, setControlDesc] = useState<any>(null);
    const theme = useTheme();

    // Function to determine the initial framework based on control_type
    const getInitialFramework = (): Framework => {
        const controlType = selectedRisk?.control_type?.toLowerCase();
        if (controlType?.includes('cis')) return 'cis';
        if (controlType?.includes('nist')) return 'nist';
        if (controlType?.includes('spark')) return 'spark';
        return 'cis'; // Default fallback
    };

    const [selectedFramework, setSelectedFramework] = useState<Framework>(getInitialFramework());

    // State for versions dropdown
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [versionList, setVersionList] = useState<VersionInfo[]>([]); // Store structured version info
    const [fetchVersionsError, setFetchVersionsError] = useState<string | null>(null);
    // State to hold the selected version key (e.g., "cisco-ios-15")
    const [selectedVersionKey, setSelectedVersionKey] = useState<string>("");

    // State for loading control description based on version selection
    const [loadingControlDesc, setLoadingControlDesc] = useState(false);

    // State for new accordions
    const [isRemediationExpanded, setIsRemediationExpanded] = useState(true); // Start expanded?
    const [isAuditExpanded, setIsAuditExpanded] = useState(false);
    const [isRationaleExpanded, setIsRationaleExpanded] = useState(false);

    // Fetch version list function - updated parsing
    const fetchVersionData = useCallback(async () => {
        const id = selectedRisk?.id;
        const moduleParam = selectedRisk?.module || 'on_premise';
        console.log('[fetchVersionData] Starting. ID:', id, 'Module:', moduleParam);
        if (!id) {
            console.log('[fetchVersionData] No ID found, returning.');
            setLoadingVersions(false);
            return;
        }

        setLoadingVersions(true);
        setVersionList([]);
        setFetchVersionsError(null);
        try {
            console.log(`[fetchVersionData] Calling API: securityscan/vendor-device-versions/${id}?module=${moduleParam}`);
            const response = await axiosInstance.get(
                `securityscan/vendor-device-versions/${id}?module=${moduleParam}`
            );
            const data = response?.data?.data;
            const parsedVersions: VersionInfo[] = [];

            if (data && typeof data === 'object') {
                for (const vendor in data) {
                    if (data[vendor] && typeof data[vendor] === 'object') {
                        for (const model in data[vendor]) {
                            if (Array.isArray(data[vendor][model])) {
                                data[vendor][model].forEach((version: any) => {
                                    if (typeof version === 'string') {
                                        parsedVersions.push({ vendor, model, version });
                                    }
                                });
                            }
                        }
                    }
                }
            }
            console.log("[fetchVersionData] Parsed versions:", parsedVersions);
            setVersionList(parsedVersions);
        } catch (error) {
            console.error(`Error fetching bench versions:`, error);
            setFetchVersionsError('Failed to load versions.');
            setVersionList([]);
        } finally {
            setLoadingVersions(false);
        }
    }, [selectedRisk?.id, selectedRisk?.module]);

    // Effect to fetch initial total details and versions list
    useEffect(() => {
        console.log('[useEffect - Initial Load] Running effect. selectedRisk:', selectedRisk);
        const fetchInitialData = async () => {
            if (!selectedRisk?.id) {
                console.log('[useEffect - Initial Load] No selectedRisk.id found, returning.');
                return;
            }
            try {
                // Fetch total details
                console.log('[useEffect - Initial Load] Fetching total details...');
                const totalResponse = await axiosInstance.get(`securityscan/control-asset-details/${selectedRisk.id}`, {
                    params: { module: selectedRisk?.module || 'on_premise' }
                });
                setTotalDetails(totalResponse.data?.data);
                console.log('[useEffect - Initial Load] Fetched total details.');

                // Fetch versions list
                console.log('[useEffect - Initial Load] Calling fetchVersionData.');
                await fetchVersionData();
                console.log('[useEffect - Initial Load] Finished fetchVersionData.');

            } catch (error) {
                console.error('[useEffect - Initial Load] Error fetching initial data:', error);
            }
        };
        fetchInitialData();
    }, [selectedRisk?.id, selectedRisk?.module, fetchVersionData]); // Only depends on ID, module, and the fetch function

    // Effect to fetch control description when a version is selected OR framework changes
    useEffect(() => {
        console.log('[useEffect - Control Desc] Running. Selected Key:', selectedVersionKey, 'Selected Framework:', selectedFramework);
        const fetchControlDetailsForVersion = async () => {
            // We need a version selected *and* the base risk info to proceed
            if (!selectedVersionKey || !selectedRisk?.id) {
                 console.log('[useEffect - Control Desc] No version selected or missing ID. Clearing description.');
                 setControlDesc(null);
                 return;
            }

            // Parse vendor, model, version from the key
            const parts = selectedVersionKey.split('-');
            if (parts.length < 3) {
                 console.error('[useEffect - Control Desc] Invalid selectedVersionKey format:', selectedVersionKey);
                 setControlDesc(null);
                 return;
            }
            const vendor = parts[0];
            const model = parts[1];
            const version = parts.slice(2).join('-');

            setLoadingControlDesc(true);
            setControlDesc(null); // Clear previous description
            try {
                 console.log(`[useEffect - Control Desc] Fetching for Framework: ${selectedFramework.toUpperCase()}, ${vendor}-${model}-${version}, ID: ${selectedRisk.id}`);
                const descResponse = await axiosInstance.get('securityscan/controls-by-parameter', {
                    params: {
                         parameter_name_id: selectedRisk.id,
                         control_type: selectedFramework.toUpperCase(),
                         category_name: 'Benchmark', // As requested
                         vendor: vendor,
                         model: model,
                         version: version,
                         module: selectedRisk?.module || 'on_premise'
                    }
                });
                setControlDesc(descResponse.data?.data);
                 console.log('[useEffect - Control Desc] Fetched description:', descResponse.data?.data);
            } catch (error) {
                 console.error('[useEffect - Control Desc] Error fetching control description:', error);
                 setControlDesc(null);
            } finally {
                 setLoadingControlDesc(false);
            }
        };

        fetchControlDetailsForVersion();

    }, [selectedVersionKey, selectedRisk?.id, selectedRisk?.module, selectedFramework]); // Depends on selected version, ID, module, AND framework

    const handleVersionChange = (event: any) => {
        const key = event.target.value as string;
        console.log("Version selected:", key);
        setSelectedVersionKey(key);
    };

    return (
        <Box sx={{ p: 2, pl: 0, pr: 0, height: '100%', display: 'flex', flexDirection: 'column',overflow: 'auto' }} className="scroll">
            {/* Header with back button */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}   
                        sx={{ 
                            color: '#666', 
                            fontWeight: 700, 
                            fontSize: '14px',
                            p: 0, 
                            minWidth: 'auto', 
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#666'
                            },
                            '&:focus': {
                                backgroundColor: 'transparent',
                                color: '#666'
                            },
                            '&:active': {
                                backgroundColor: 'transparent',
                                color: '#666'
                            }
                        }}
                        onClick={() => handlleSeeDetailsClose(false)}
                    >
                        Back to Assets
                    </Button>
                </Box>
            </Box>
            <Divider sx={{ width: '100%' }} />

            <DetailsFlyout Total_Resources={totalDetails} id={selectedRisk.id} module={selectedRisk?.module || 'on_premise'}/>

            {/* Statistics Cards */}
            {/* <Box sx={{ mb: 0, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex' }}>
                    <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Total Resources</Typography>
                        <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>{totalDetails?.devices_count || 0}</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ height: '40px', borderRight: '1px solid #e0e0e0', mt: 1 }} />
                    <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Failed</Typography>
                        <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 'bold' }}>{totalDetails?.failed_count || 0}</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ height: '40px', borderRight: '1px solid #e0e0e0', mt: 1 }} />
                    <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Pass</Typography>
                        <Typography variant="h5" sx={{ color: '#4caf50', fontWeight: 'bold' }}>{totalDetails?.passed_count || 0}</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ height: '40px', borderRight: '1px solid #e0e0e0', mt: 1 }} />
                    <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Accepted Risk</Typography>
                        <Typography variant="h5" sx={{ color: '#e91e63', fontWeight: 'bold' }}>{totalDetails?.accepted_count || 0}</Typography>
                    </Box>
                </Box>
            </Box> */}
            {/* <Divider sx={{ width: '100%' }} /> */}
        </Box>
    );
};

export default SeeDetailsDrawer;